import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ProducerLayout } from '@/components/producer';
import { DangerZoneSection } from '@/components/producer/DangerZoneSection';
import { AlertCircle, Loader2 } from 'lucide-react';
import { PublishReadinessCard } from '@/components/producer/PublishReadinessCard';
import { useProducerById } from '@/hooks/useMyProducers';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { usePublishProducer, useUnpublishProducer } from '@/hooks/usePublishProducer';
import {
  PublishingSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { ProducerSectionHeader } from '@/components/producer/ProducerSectionHeader';
import { useProducerFormData } from '@/hooks/useProducerFormData';

export default function ProducerPublicering() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const { capabilities } = useProducerAccess(farmId);
  const { meta, checklist, hasCapability, invalidate } = useProducerCapabilities(farmId);

  const publishMutation = usePublishProducer();
  const unpublishMutation = useUnpublishProducer();

  const isDemo = farmId === 'demo';

  const { initialFormData } = useProducerFormData(
    producer,
    isDemo,
    farmId
  );

  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async () => { /* Publishing handled via RPCs */ },
  });

  const handleTogglePublished = async (isPublic: boolean) => {
    if (isDemo) {
      editorState.updateField('status', isPublic ? 'public' : 'draft');
      return;
    }
    if (!producer) return;

    try {
      if (isPublic) {
        await publishMutation.mutateAsync(producer.id);
      } else {
        await unpublishMutation.mutateAsync(producer.id);
      }
      editorState.updateField('status', isPublic ? 'public' : 'draft');
      invalidate();
    } catch {
      // Error handled by mutation hooks (toast)
    }
  };

  if (isLoading && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Publicering">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Publicering">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Kunde inte ladda gården.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Försök igen
        </Button>
      </ProducerLayout>
    );
  }

  const isSubmitting = publishMutation.isPending || unpublishMutation.isPending;


  return (
    <ProducerLayout
      farmId={farmId || 'demo'}
      producerName={editorState.formData.name || 'Din gård'}
      title="Publicering"
    >
        <ProducerSectionHeader
          title="Publicering"
          description="Hantera synlighet och förhandsgranskning."
          
        />

        {/* Canonical publish readiness from producer_capabilities_v */}
        {meta && checklist && farmId && (
          <PublishReadinessCard
            farmId={farmId}
            meta={meta}
            checklist={checklist}
            canPublish={hasCapability('publish.producer')}
            canUnpublish={hasCapability('unpublish.producer')}
          />
        )}

        <PublishingSection
          formData={editorState.formData}
          updateField={editorState.updateField}
          producerSlug={producer?.slug}
          producerId={producer?.id}
          isAdmin={false}
          producerStatus={producer?.producerStatus}
          onStatusChange={handleTogglePublished}
          isSubmitting={isSubmitting}
          coverImageUrl={undefined}
          placeId={producer?.place_id}
          shortDescription={producer?.short_description}
        />

        {/* Danger Zone - only for owner */}
        {!isDemo && producer && (
          <div className="mt-8">
            <DangerZoneSection
              producerId={producer.id}
              producerName={producer.name}
              producerStatus={editorState.formData.status}
              canManage={capabilities.canManageDangerZone}
              onArchived={() => refetch()}
              onDeleted={() => navigate('/app/producer')}
            />
          </div>
        )}
    </ProducerLayout>
  );
}
